import { motion } from "framer-motion";
import { ReceiptText, ShieldOff, Split } from "lucide-react";

const STEPS = [
  {
    num: "01",
    icon: ReceiptText,
    title: "Purchase of Future Revenue",
    body: "This is not a loan. A funder purchases a fixed slice of your future receivables at a discount — an off-balance-sheet asset sale, so nothing compounds and nothing accrues interest against you.",
  },
  {
    num: "02",
    icon: ShieldOff,
    title: "Unsecured Zero-Asset Structure",
    body: "No blanket UCC-1 hard-asset seizures. No real estate pledge. No equipment liens. Your approval is underwritten against the strength of your revenue — not the things you own.",
  },
  {
    num: "03",
    icon: Split,
    title: "Flexible ACH Splits",
    body: "Remittance flexes with your deposits. When revenue dips seasonally, your daily split dips with it — the structure breathes with your business instead of suffocating it.",
  },
];

export default function McaExplainerGrid() {
  return (
    <section id="structure" className="py-24 sm:py-32" data-testid="mca-explainer-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          <p className="eyebrow mb-6">The instrument</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight leading-tight text-zinc-100">
            The Merchant Cash Advance,{" "}
            <span className="font-editorial italic text-amber-400">engineered in three moves.</span>
          </h2>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {STEPS.map((step, i) => (
            <motion.article
              key={step.num}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6 }}
              className="group relative glass-panel rounded-md p-8 hover:border-amber-500/40 transition-colors duration-500"
              data-testid={`mca-step-${step.num}`}
            >
              <div className="flex items-start justify-between mb-10">
                <span className="font-display text-5xl font-extralight text-outline group-hover:text-amber-400/80 group-hover:[-webkit-text-stroke:0px] transition-all duration-500">
                  {step.num}
                </span>
                <step.icon className="w-6 h-6 text-zinc-600 group-hover:text-amber-400 transition-colors duration-500" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-medium tracking-tight text-zinc-100 mb-4">
                {step.title}
              </h3>
              <p className="text-sm leading-relaxed text-zinc-400">{step.body}</p>
              <div className="absolute bottom-0 left-0 h-px w-0 bg-amber-500 group-hover:w-full transition-all duration-700" />
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
